import React, { useState } from 'react';
import {
  RiArrowGoBackLine,
  RiAlertLine,
  RiCalendarLine,
  RiCheckboxLine,
  RiCheckboxBlankLine
} from 'react-icons/ri';
import Modal from '../ui/Modal';
import { ReturnItemIdentity } from './ReturnItemIdentity';
import LoadingButton from '../ui/LoadingButton';
import { showAlert } from '../../utils/alert';
import { getLocalDateString } from '../../utils/dateUtils';
import { cn } from '../../utils/cn';

interface ReturnEquipmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: ReturnFormData) => Promise<void>;
  rental: {
    id: number;
    rental_number: string;
    rental_items?: Array<{
      id: number;
      device?: {
        id: number;
        code: string;
        serial_number: string;
        model?: string;
      };
      // L3: รายการที่มีแต่ซิม (ไม่มีเครื่อง) ก็ต้องตามเก็บ — เดิมหน้านี้ไม่แสดงเลย
      // ทำให้ backend ที่นับซิมด้วยจะบล็อกการปิดสัญญาโดยผู้ใช้ไม่มีทางติ๊ก
      sim?: {
        id: number;
        phone_number?: string;
        code?: string;
      };
      product_name?: string;
    }>;
  };
}

// สภาพอุปกรณ์ที่รับคืน — ค่าเดียวกับ EquipmentCondition ของใบตามเก็บ (backend enums)
export type ReturnEquipmentCondition = 'GOOD' | 'DAMAGED' | 'LOST';

interface ReturnFormData {
  return_date: string;
  return_reason: string;
  return_reason_other?: string;
  notes?: string;
  returned_items: number[]; // device_ids ที่คืน — backend บันทึกรับคืนในใบตามเก็บให้อัตโนมัติ (E2E v6 PRE-1)
  // L3: rental_item_id ที่คืน — จำเป็นสำหรับรายการที่มีแต่ซิม (ไม่มี device_id ให้ส่ง)
  // ส่งคู่กับ returned_items เสมอ backend รวมทั้งสองชุด
  returned_item_ids: number[];
  equipment_condition: ReturnEquipmentCondition; // ใช้กับทุกรายการที่ติ๊กในครั้งนี้ (default GOOD)
}

const RETURN_REASONS = [
  { value: 'contract_expired', label: 'หมดสัญญา' },
  { value: 'switch_provider', label: 'ลูกค้าเปลี่ยนไปใช้บริการที่อื่น' },
  { value: 'contract_cancelled', label: 'ลูกค้าถูกผู้ว่าจ้างยกเลิก' },
  { value: 'other', label: 'อื่นๆ' }
];

const EQUIPMENT_CONDITIONS: Array<{ value: ReturnEquipmentCondition; label: string }> = [
  { value: 'GOOD', label: 'สภาพดี' },
  { value: 'DAMAGED', label: 'ชำรุด' },
  { value: 'LOST', label: 'สูญหาย' }
];

const ReturnEquipmentModal: React.FC<ReturnEquipmentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  rental
}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // อุปกรณ์ที่คืนได้ = rental_items ที่มีของจริงให้เก็บ (เครื่อง **หรือ** ซิม)
  // rental บางรายการไม่ผูกของเลย (software-only / บริการล้วน) → ไม่มีของให้ติ๊ก
  // แต่ "ปิดการเช่า" ยังมีความหมาย (หยุดบิล + เสร็จสิ้น) และ backend รองรับ
  const returnableItems = rental.rental_items?.filter(item => item.device?.id || item.sim?.id) ?? [];
  const hasReturnableItems = returnableItems.length > 0;

  const [formData, setFormData] = useState<ReturnFormData>({
    return_date: getLocalDateString(),
    return_reason: 'contract_expired',
    notes: '',
    returned_items: returnableItems.map(item => item.device?.id || 0).filter(id => id > 0),
    returned_item_ids: returnableItems.map(item => item.id),
    equipment_condition: 'GOOD'
  });

  // ติ๊กเป็นราย "รายการเช่า" (rental_item) — รายการซิมล้วนไม่มี device_id ให้ใช้เป็นกุญแจ
  const handleCheckItem = (itemId: number, deviceId?: number) => {
    setFormData(prev => {
      const checked = prev.returned_item_ids.includes(itemId);
      return {
        ...prev,
        returned_item_ids: checked
          ? prev.returned_item_ids.filter(id => id !== itemId)
          : [...prev.returned_item_ids, itemId],
        returned_items: deviceId
          ? (checked
              ? prev.returned_items.filter(id => id !== deviceId)
              : [...prev.returned_items, deviceId])
          : prev.returned_items,
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation - use inline errors instead of showAlert
    const newErrors: Record<string, string> = {};

    if (!formData.return_date) {
      newErrors.return_date = 'กรุณาระบุวันที่คืน';
    } else if (formData.return_date > getLocalDateString()) {
      // BUG-S2253-001: วันที่คืน = ของกลับถึงมือแล้ว — backend ปฏิเสธวันในอนาคต (422) · บอกที่ช่องก่อนส่ง
      newErrors.return_date = 'วันที่คืนต้องไม่เป็นวันในอนาคต — บันทึกได้เฉพาะของที่กลับถึงมือแล้ว';
    }

    // ลำดับการตรวจ = ลำดับช่องบนจอ (สาเหตุอื่นๆ อยู่เหนือรายการอุปกรณ์) — focus ช่องแรกที่ผิด (BUG-S2230-001)
    if (formData.return_reason === 'other' && !formData.return_reason_other?.trim()) {
      newErrors.return_reason_other = 'กรุณาระบุสาเหตุอื่นๆ';
    }

    // บังคับเลือกอุปกรณ์ "เฉพาะ" เมื่อรายการเช่ามีอุปกรณ์ให้คืน
    // ถ้าไม่มีอุปกรณ์ผูก (software-only) → ปิดการเช่าได้เลย ไม่ต้องติ๊ก
    if (hasReturnableItems && formData.returned_item_ids.length === 0) {
      newErrors.returned_items = 'กรุณาเลือกอุปกรณ์ที่ต้องการคืนอย่างน้อย 1 รายการ';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to first error
      const firstErrorField = Object.keys(newErrors)[0];
      // BUG-S2166-002: กล่องรายการอุปกรณ์ไม่ใช่ input จึงมีแค่ data-name — เดิมหาแค่ [name] เลยไม่เลื่อนจอ/ไม่ focus
      const errorElement = document.querySelector(
        `[name="${firstErrorField}"], [data-name="${firstErrorField}"]`
      );
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (errorElement as HTMLInputElement).focus();
      }
      return;
    }

    setLoading(true);
    try {
      await onConfirm({ ...formData });
      onClose();
    } catch (error) {
      console.error('Error returning equipment:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    // G4 (MANUAL_AUDIT_SYSTEM_FINDINGS): เดิมมีหัวข้อ + ปุ่มปิดซ้อนกัน 2 ชั้น — header ของ
    // <Modal> เอง กับ <h3>+ปุ่มปิดที่เขียนซ้ำในเนื้อโมดัล (ผู้ใช้เห็น "คืนอุปกรณ์ - RT-xxxx"
    // สองบรรทัดติดกัน และมีปุ่ม aria-label="ปิด" ซ้ำสองอันให้ screen reader)
    // → เหลือชั้นเดียว ย้ายไอคอนขึ้นไปอยู่ใน title ของ Modal
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="flex items-center">
          <RiArrowGoBackLine className="mr-2 h-6 w-6 text-primary-600" />
          คืนอุปกรณ์ - {rental.rental_number}
        </span>
      }
      size="lg"
    >
      <form onSubmit={handleSubmit} noValidate>
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* วันที่คืน */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              <RiCalendarLine className="inline h-4 w-4 mr-1" />
              วันที่คืน *
            </label>
            <input
              type="date"
              name="return_date"
              value={formData.return_date}
              max={getLocalDateString()}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, return_date: e.target.value }));
                setErrors(prev => ({ ...prev, return_date: '' }));
              }}
              className={cn('input', errors.return_date && 'border-danger-500')}
              aria-invalid={Boolean(errors.return_date)}
              aria-describedby={errors.return_date ? 'return_date-error' : undefined}
            />
            {errors.return_date && (
              <p id="return_date-error" className="mt-1 text-sm text-danger-600">{errors.return_date}</p>
            )}
          </div>

          {/* สาเหตุการคืน */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              สาเหตุการคืน *
            </label>
            <select
              aria-label="สาเหตุการคืน"
              name="return_reason"
              value={formData.return_reason}
              onChange={(e) => setFormData(prev => ({ ...prev, return_reason: e.target.value }))}
              className="select"
            >
              {RETURN_REASONS.map(reason => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>

            {formData.return_reason === 'other' && (
              <>
                <input
                  type="text"
                  name="return_reason_other"
                  placeholder="ระบุสาเหตุอื่นๆ"
                  value={formData.return_reason_other || ''}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, return_reason_other: e.target.value }));
                    setErrors(prev => ({ ...prev, return_reason_other: '' }));
                  }}
                  className={cn('input mt-2', errors.return_reason_other && 'border-danger-500')}
                  aria-invalid={Boolean(errors.return_reason_other)}
                  aria-describedby={errors.return_reason_other ? 'return_reason_other-error' : undefined}
                />
                {errors.return_reason_other && (
                  <p id="return_reason_other-error" className="mt-1 text-sm text-danger-600">{errors.return_reason_other}</p>
                )}
              </>
            )}
          </div>

          {/* รายการอุปกรณ์ที่คืน */}
          <div>
            <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-3">
              <div className="w-8 h-8 bg-gradient-to-br from-warning-500 to-warning-600 rounded-lg flex items-center justify-center shadow-sm">
                <RiCheckboxLine className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-base font-semibold text-secondary-800">รายการอุปกรณ์ที่คืน</h3>
            </div>
            {hasReturnableItems ? (
              <>
                <div
                  tabIndex={-1}
                  data-name="returned_items"
                  className={`space-y-3 border rounded-lg p-4 ${
                  errors.returned_items ? 'border-danger-500' : 'border-secondary-200'
                }`}>
                  {returnableItems.map((item) => {
                    const deviceId = item.device?.id;
                    const isChecked = formData.returned_item_ids.includes(item.id);
                    const simLabel = item.sim?.phone_number || item.sim?.code;

                    return (
                      <div key={item.id} className="space-y-2">
                        <div className="flex items-start">
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked={isChecked}
                            aria-label={`เลือกคืน ${item.device?.code || simLabel || item.product_name || 'รายการ'}`}
                            onClick={() => {
                              handleCheckItem(item.id, deviceId);
                              setErrors(prev => ({ ...prev, returned_items: '' }));
                            }}
                            className="mt-1 mr-3"
                          >
                            {isChecked ? (
                              <RiCheckboxLine className="h-5 w-5 text-primary-600" />
                            ) : (
                              <RiCheckboxBlankLine className="h-5 w-5 text-secondary-400" />
                            )}
                          </button>
                          {/* แสดง "ของแต่ละชิ้น" แยกบรรทัด ไม่ใช่ต่อท้ายกันด้วย " | ซิม: …"
                              เพราะตั้งแต่แยกแถว รายการเช่า 1 รายการที่มีทั้งเครื่องและซิม
                              = ใบตามเก็บ 2 แถว ผู้ใช้ต้องเห็นว่ากำลังปิดของ 2 ชิ้นด้วยการติ๊กครั้งเดียว */}
                          <div className="flex-1 space-y-1">
                            <div className="font-medium text-secondary-900">
                              {item.product_name || (item.device ? 'อุปกรณ์' : 'ซิม')}
                            </div>
                            {item.device && (
                              <ReturnItemIdentity
                                item={{
                                  device_id: item.device.id,
                                  device: {
                                    code: item.device.code,
                                    // BUG-S2166-001: ReturnItemIdentity เติม "S/N " เอง — ส่ง serialText() (ที่เติม "S/N: ")
                                    // เข้าไปอีกชั้นเลยขึ้น "S/N S/N: xxx" · ส่ง S/N ดิบ (ตัดช่องว่าง) เท่านั้น
                                    serial_number: item.device.serial_number?.toString().trim() || undefined,
                                    model: item.device.model,
                                  },
                                }}
                              />
                            )}
                            {item.sim && (
                              <ReturnItemIdentity
                                item={{
                                  sim_id: item.sim.id,
                                  sim: { phone_number: simLabel },
                                }}
                              />
                            )}
                            {item.device && item.sim && (
                              <p className="text-xs text-warning-700">
                                ติ๊กช่องนี้ = ปิดทั้งเครื่องและซิมด้วยสภาพเดียวกัน
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {errors.returned_items && (
                  <p className="mt-1 text-sm text-danger-600">{errors.returned_items}</p>
                )}
                {/* E2E v6 PRE-1 (option ก): backend บันทึกรับคืนในใบตามเก็บให้รายการที่ติ๊ก — รายการที่ไม่ติ๊กจะติด 400 */}
                <p className="mt-2 text-xs text-secondary-500">
                  รายการที่ติ๊ก ระบบจะบันทึกรับคืนในใบตามเก็บอุปกรณ์ให้อัตโนมัติ · รายการที่ไม่ติ๊กจะยังปิดการเช่าไม่ได้
                </p>
                {/* จอนี้ใช้ "สภาพเดียว" กับทุกรายการที่ติ๊ก (payload มีช่อง equipment_condition ช่องเดียว)
                    → เคส "เครื่องคืนดี แต่ซิมหาย" ทำที่จอนี้ไม่ได้ ต้องบอกทางออกให้ชัด
                    ไม่งั้นผู้ใช้จะติ๊กทั้งคู่เป็น "สภาพดี" แล้วซิมที่หายจะกลับเข้าคลัง */}
                {returnableItems.some(item => item.device && item.sim) && (
                  <p className="mt-1 text-xs text-warning-700">
                    หมายเหตุ: จอนี้บันทึก “สภาพเดียว” ให้ทุกรายการที่ติ๊ก —
                    ถ้าได้เครื่องคืนแต่ซิมหาย (หรือกลับกัน) ให้ไปบันทึกแยกชิ้นที่เมนู
                    “ติดตามการคืนอุปกรณ์” ซึ่งแยกเครื่องกับซิมเป็นคนละรายการแล้ว
                  </p>
                )}
                <div className="mt-3">
                  <label htmlFor="return-equipment-condition" className="block text-sm font-medium text-secondary-700 mb-2">
                    สภาพอุปกรณ์ที่รับคืน
                  </label>
                  <select
                    id="return-equipment-condition"
                    name="equipment_condition"
                    value={formData.equipment_condition}
                    onChange={(e) =>
                      setFormData(prev => ({
                        ...prev,
                        equipment_condition: e.target.value as ReturnEquipmentCondition
                      }))
                    }
                    className="select"
                  >
                    {EQUIPMENT_CONDITIONS.map(c => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-secondary-500">
                    ใช้กับทุกรายการที่ติ๊กในครั้งนี้ — ชำรุด/สูญหาย เครื่องจะไม่กลับเข้าคลังพร้อมให้เช่า
                  </p>
                </div>
              </>
            ) : (
              <div className="border border-dashed border-secondary-300 rounded-lg px-4 py-5 text-center bg-secondary-50">
                <p className="text-sm text-secondary-600">
                  รายการเช่านี้ไม่มีอุปกรณ์ผูกอยู่ (บริการ/ซอฟต์แวร์ล้วน)
                </p>
                <p className="text-xs text-secondary-500 mt-1">
                  กด &quot;ปิดการเช่า&quot; เพื่อจบรายการและหยุดคิดเงินได้เลย
                </p>
              </div>
            )}
          </div>

          {/* หมายเหตุเพิ่มเติม */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              หมายเหตุเพิ่มเติม
            </label>
            <textarea
              aria-label="หมายเหตุเพิ่มเติม"
              name="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              className="input"
              placeholder="ระบุรายละเอียดเพิ่มเติม..."
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t space-y-3">
          {/* คำเตือน: การยืนยันเป็นการกระทำที่ย้อนกลับไม่ได้ */}
          <div className="flex items-start gap-2 bg-warning-50 border border-warning-300 rounded-md px-3 py-2">
            <RiAlertLine className="h-4 w-4 text-warning-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-warning-800">
              ยืนยันแล้ว: รายการเช่าจะเป็น &quot;เสร็จสิ้น&quot; และหยุดคิดเงิน — แก้ไม่ได้
            </p>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50"
            >
              ยกเลิก
            </button>
            <LoadingButton
              type="submit"
              loading={loading}
              className="px-4 py-2 bg-success-700 text-white rounded-lg hover:bg-success-800"
            >
              <RiArrowGoBackLine className="mr-2 h-4 w-4" />
              {hasReturnableItems ? 'บันทึกการคืน' : 'ปิดการเช่า'}
            </LoadingButton>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default ReturnEquipmentModal;